import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import httpService from './httpService'
import { ClubQueryKeys } from './club'
import { PlayerQueryKeys } from './player'
import { Response, Result } from '@/types'

/**
 * Client mirror of the server's grantable club-capability system. The schema is
 * the source of truth: clients render forms and gate UI from it rather than
 * hardcoding any specific capability, and the server only returns capabilities
 * a club has actually been granted (so a non-enabled club learns nothing).
 */

// Closed set of input widgets, discriminated by `kind` (mirrors the server's
// sealed CapabilityFieldType).
export type CapabilityFieldType =
  | { kind: 'int'; min?: number; max?: number }
  | { kind: 'decimal'; min?: number; max?: number; scale?: number }
  | { kind: 'boolean' }
  | { kind: 'text'; maxLength?: number }
  | { kind: 'url' }
  | { kind: 'enum'; options: CapabilityOption[]; multiple?: boolean }
  | { kind: 'catalog'; catalogKey: string; multiple?: boolean; allowOther?: boolean }

export interface CapabilityOption {
  value: string
  label: string
}

export interface CapabilityField {
  key: string
  label: string
  type: CapabilityFieldType
  required?: boolean
  description?: string | null
  default?: unknown
  locked?: boolean
}

/**
 * Widgets for an ACTION's strongly-typed inputs — distinct from the club-CONFIG
 * fields above (which still use {@link CapabilityFieldType}). Same discriminated
 * `kind` union minus `catalog`: action inputs are never open-vocabulary selects.
 * Used both for an action's declared `inputFields` and for the delta-driven extra
 * `requiredInputs` a live preview demands (e.g. a film URL for a large change).
 */
export type ActionInputType =
  | { kind: 'int'; min?: number; max?: number }
  | { kind: 'decimal'; min?: number; max?: number; scale?: number }
  | { kind: 'boolean' }
  | { kind: 'text'; maxLength?: number }
  | { kind: 'url' }
  | { kind: 'enum'; options: CapabilityOption[]; multiple?: boolean }

/** One strongly-typed input for a capability action (a base field or a preview-required extra). */
export interface ActionInputField {
  key: string
  label: string
  type: ActionInputType
  required?: boolean
  description?: string | null
  default?: unknown
}

export type CapabilityActionTarget = 'MEMBER'

export interface CapabilityAction {
  key: string
  label: string
  description?: string
  target: CapabilityActionTarget
  inputFields: ActionInputField[]
  requiredPermissionUrn: string
}

export type QuotaLevel = 'CLUB' | 'INDIVIDUAL'

export interface QuotaSpec {
  fieldKey: string | null
  resourceUrn: string
  level: QuotaLevel
}

export interface ClubCapabilityDefinitionSchema {
  key: string
  capabilityUrn: string
  label: string
  description?: string
  category: string
  fields: CapabilityField[]
  conferredRoles: string[]
  quotas: QuotaSpec[]
  actions: CapabilityAction[]
}

export interface ClubCapabilitySchemaResponse {
  schemaVersion: string
  capabilities: ClubCapabilityDefinitionSchema[]
}

export interface ClubSeatUsage {
  allocated: number
  used: number
  remaining: number
}

export interface ClubQuotaDescriptor extends ClubSeatUsage {
  resourceUrn: string
  label: string
  level: QuotaLevel
  holderUrn: string | null
}

/** One RBAC capability role a member holds, in human-friendly form (labels from the server). */
export interface CapabilityRoleView {
  roleUrn: string
  label: string
  capabilityKey: string
  capabilityLabel: string
  description?: string | null
  /** Human-readable permissions this role grants, when the server provides them. */
  permissions?: string[] | null
  seats?: ClubSeatUsage | null
}

/** A club member (manager) and the capability roles they hold. */
export interface ClubMemberCapabilityRoles {
  userId: number
  fullName?: string | null
  duprId?: string | null
  roles: CapabilityRoleView[]
}

/**
 * An eligible club member with the display fields the picker needs, sourced directly from the
 * server's ES-backed eligibility index — so the eligible list is self-describing and needs no
 * separate member lookup/join.
 */
export interface EligibleMember {
  userId: number
  fullName: string
  duprId?: string | null
  singles: string | number | null
  doubles: string | number | null
  // Whether the member is eligible for the action, and (if not) every rule they fail.
  eligible: boolean
  reasons: string[]
}

export type EligibleMembersPage = Result & { hits: EligibleMember[] }

export interface MemberActionResult {
  auditId: number
  obfuscatedSinglesMatchId?: number
  obfuscatedDoublesMatchId?: number
  /** APPLIED when the action took effect; PENDING_APPROVAL when it was held for an admin. */
  status?: string
}

export enum CapabilityQueryKeys {
  CLUB_CAPABILITY_SCHEMA = 'club_capability_schema',
  CLUB_QUOTAS = 'club_quotas',
  CLUB_ELIGIBILITY = 'club_eligibility',
  CLUB_CAPABILITY_ROLES = 'club_capability_roles',
  CLUB_MEMBER_ACTION_AUDIT = 'club_member_action_audit',
  CLUB_MEMBER_ACTION_PREVIEW = 'club_member_action_preview',
}

/**
 * A dry-run verdict for a proposed member action, so the UI can warn a coach BEFORE they submit
 * that their action would be blocked (over a club limit) or held for admin approval. Capability-
 * agnostic: it carries only the generic outcome + a human message the server composes.
 */
export interface MemberActionPreview {
  /** APPLY (will take effect), REQUIRES_APPROVAL (held for an admin), or BLOCKED (would be rejected). */
  outcome: string
  message: string
  requiresApproval: boolean
  blocked: boolean
  /**
   * Extra strongly-typed inputs THIS specific change demands, self-describing so the client can
   * render + validate them directly. Delta-driven (dynamic requiredness), e.g. a change over the
   * band threshold returns a required film URL + reason. Empty/absent when nothing extra is needed.
   */
  requiredInputs?: ActionInputField[]
  /**
   * Machine-readable cause of a BLOCKED verdict: LIMIT = a hard rule cap; CREDIT = out of credits /
   * needs subscription (the paywall signal); MEMBERSHIP = not an adjustable member. Absent when not
   * blocked.
   */
  reasonCode?: 'LIMIT' | 'CREDIT' | 'MEMBERSHIP'
  /** Machine code for the credit the change would spend (e.g. "complimentary"), when credit-metered. */
  creditType?: string | null
  /** Human label for that credit type (e.g. "Complimentary"), for display. */
  creditTypeLabel?: string | null
  /** How many of that credit the acting coach has left. */
  creditRemaining?: number | null
}

/**
 * One member-action history/audit row. Capability-agnostic: every member-acting capability writes
 * the common who/when/status fields; the rating-specific fields are populated only for the
 * rating-adjustment capability (other capabilities leave them null).
 */
export interface MemberActionAuditView {
  id: number
  timestamp?: string | null
  coachName?: string | null
  coachId?: number | null
  recipientName?: string | null
  recipientId?: number | null
  oldSingles?: number | null
  newSingles?: number | null
  oldDoubles?: number | null
  newDoubles?: number | null
  reason?: string | null
  status: string
  singlesMatchId?: number | null
  doublesMatchId?: number | null
}

/** An audit row tagged with the capability it belongs to (the endpoint is per-capability). */
export interface MemberActionAuditEntry extends MemberActionAuditView {
  capabilityKey: string
  capabilityLabel: string
}

const capabilitiesPath = (clubId: number | string) => `/club/${clubId}/capabilities`

/** A club's granted capabilities + their declared actions (visibility-filtered, no-leak). */
export const useClubCapabilitySchema = (clubId: number | string, enabled: boolean) =>
  useQuery(
    [CapabilityQueryKeys.CLUB_CAPABILITY_SCHEMA, clubId],
    async ({ signal }) => {
      const res = await httpService.Get<Response & { result: ClubCapabilitySchemaResponse }>(
        `${capabilitiesPath(clubId)}/schema`,
        { signal }
      )
      return res.result
    },
    { enabled: enabled && !!clubId, retry: false, refetchOnWindowFocus: false }
  )

/**
 * The RBAC capability roles held by the club's managers, in human-friendly form.
 * Returns a collection, so the server wraps it as `results` (ArrayWrapper), not
 * the singular `result` used for object/map responses.
 */
export const useClubCapabilityRoles = (clubId: number | string, enabled: boolean) =>
  useQuery(
    [CapabilityQueryKeys.CLUB_CAPABILITY_ROLES, clubId],
    async ({ signal }) => {
      const res = await httpService.Get<Response & { results?: ClubMemberCapabilityRoles[] }>(
        `${capabilitiesPath(clubId)}/roles`,
        { signal }
      )
      return res.results ?? []
    },
    { enabled: enabled && !!clubId, retry: false, refetchOnWindowFocus: false }
  )

/** A club's seat allocations with live usage (club pool + per-coach seats). Collection ⇒ `results`. */
export const useClubQuotas = (clubId: number | string, enabled: boolean) =>
  useQuery(
    [CapabilityQueryKeys.CLUB_QUOTAS, clubId],
    async ({ signal }) => {
      const res = await httpService.Get<Response & { results?: ClubQuotaDescriptor[] }>(
        `${capabilitiesPath(clubId)}/quotas`,
        { signal }
      )
      return res.results ?? []
    },
    { enabled: enabled && !!clubId, retry: false, refetchOnWindowFocus: false }
  )

export interface EligibleMembersParams {
  offset?: number
  limit?: number
  query?: string
}

/**
 * Fetch one page of members eligible for a capability's action. ES-backed and paginated server-side
 * (so it scales to clubs with thousands of members), and each row carries display fields — no
 * separate member listing to join against. Gated by the action's permission: a caller who isn't
 * entitled gets a 403, surfaced as a query error the caller can treat as "no action available".
 */
export const fetchEligibleMembers = async (
  clubId: number | string,
  capabilityKey: string,
  params: EligibleMembersParams,
  signal?: AbortSignal
): Promise<EligibleMembersPage> => {
  const res = await httpService.Get<Response & { result: EligibleMembersPage }>(`/club/${clubId}/eligibility`, {
    signal,
    params: {
      capability: capabilityKey,
      offset: params.offset ?? 0,
      limit: params.limit ?? 25,
      query: params.query || undefined,
    },
  })
  return res.result
}

/** Paginated, ES-backed eligible-member list for a single capability's action. */
export const useEligibleMembers = (
  clubId: number | string,
  capabilityKey: string,
  params: EligibleMembersParams,
  enabled: boolean
) =>
  useQuery(
    [CapabilityQueryKeys.CLUB_ELIGIBILITY, clubId, capabilityKey, params.offset, params.limit, params.query],
    ({ signal }) => fetchEligibleMembers(clubId, capabilityKey, params, signal),
    {
      enabled: enabled && !!clubId && !!capabilityKey,
      retry: false,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  )

/**
 * Whether the viewer may perform each capability's member action, by probing the (permission-gated)
 * eligibility endpoint for one row per capability. A 403 ⇒ not permitted (hidden); a success ⇒
 * usable. Cheap (ES-backed, limit 1), and avoids a separate permission endpoint.
 */
export const usePermittedCapabilities = (
  clubId: number | string,
  capabilityKeys: string[],
  enabled: boolean
): { permitted: Set<string>; isLoading: boolean } => {
  const probes = useQueries({
    queries: capabilityKeys.map((key) => ({
      queryKey: [CapabilityQueryKeys.CLUB_ELIGIBILITY, clubId, key, 'gate'],
      queryFn: () => fetchEligibleMembers(clubId, key, { limit: 1 }),
      enabled: enabled && !!clubId,
      retry: false,
      refetchOnWindowFocus: false,
    })),
  })
  const permitted = new Set(capabilityKeys.filter((_, i) => probes[i]?.isSuccess))
  const isLoading = probes.some((p) => p.isLoading && p.fetchStatus !== 'idle')
  return { permitted, isLoading }
}

export interface PerformMemberActionPayload {
  clubId: number | string
  userId: number
  capability: string
  action: string
  values: Record<string, unknown>
}

/** Perform a capability's action on a club member (e.g. adjust a rating). */
export const usePerformMemberAction = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ clubId, userId, capability, action, values }: PerformMemberActionPayload) => {
      const res = await httpService.Post<Response & { result: MemberActionResult }>(
        `/club/${clubId}/members/${userId}/actions`,
        { capability, action, values }
      )
      return res.result
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([CapabilityQueryKeys.CLUB_ELIGIBILITY, variables.clubId])
        queryClient.invalidateQueries([CapabilityQueryKeys.CLUB_QUOTAS, variables.clubId])
        queryClient.invalidateQueries([CapabilityQueryKeys.CLUB_MEMBER_ACTION_AUDIT, variables.clubId])
        // A tune changes the member's rating, so refetch the views that show it: the club members
        // LIST (ES-backed management query, keyed by clubId) and the member DETAIL header (the player
        // profile, keyed by player id). Invalidate the key prefixes so every paged/variant entry is
        // refetched. The effect is applied async over a queue, so a brief stale window may remain even
        // after refetch — this just guarantees we never keep serving the old cache indefinitely.
        queryClient.invalidateQueries([ClubQueryKeys.GET_CLUB_MEMBERS_MANAGEMENT, Number(variables.clubId)])
        queryClient.invalidateQueries([ClubQueryKeys.GET_CLUB_MEMBERS_BY_STATE, Number(variables.clubId)])
        queryClient.invalidateQueries([PlayerQueryKeys.GET_PLAYER_PROFILE, String(variables.userId)])
      },
    }
  )
}

/**
 * Live dry-run of a proposed member action so the modal can warn the coach pre-submit. Debounces the
 * (still-being-edited) values, then probes the server's preview endpoint, returning its generic
 * verdict. The caller gates with `enabled` (passing false when field validation fails or there are no
 * values) so we never probe a payload the server would 400 on; if it 400s anyway, we surface no
 * verdict (the query simply errors and the UI ignores it). Capability-agnostic — same payload shape
 * as {@link usePerformMemberAction}.
 */
export const useMemberActionPreview = (
  clubId: number | string,
  userId: number,
  capability: string,
  action: string,
  values: Record<string, unknown>,
  enabled: boolean,
  debounceMs = 350
) => {
  // Debounce the values so we don't probe on every keystroke while the coach is still typing.
  const [debouncedValues, setDebouncedValues] = useState(values)
  const serializedValues = JSON.stringify(values)
  useEffect(() => {
    const id = setTimeout(() => setDebouncedValues(values), debounceMs)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedValues, debounceMs])

  return useQuery(
    [
      CapabilityQueryKeys.CLUB_MEMBER_ACTION_PREVIEW,
      clubId,
      userId,
      capability,
      action,
      JSON.stringify(debouncedValues),
    ],
    async ({ signal }): Promise<MemberActionPreview> => {
      const res = await httpService.Post<Response & { result: MemberActionPreview }>(
        `/club/${clubId}/members/${userId}/actions/preview`,
        { capability, action, values: debouncedValues },
        { signal }
      )
      return res.result
    },
    {
      enabled: enabled && !!clubId && !!capability && !!action,
      retry: false,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  )
}

export interface MemberActionAuditParams {
  /** Restrict to a single member (obfuscated id); omit for the whole club. */
  userId?: number
  /** Restrict to one lifecycle status (e.g. PENDING_APPROVAL). */
  status?: string
}

const fetchMemberActionAudit = async (
  clubId: number | string,
  capabilityKey: string,
  params: MemberActionAuditParams,
  signal?: AbortSignal
): Promise<MemberActionAuditView[]> => {
  // The endpoint returns a collection, so the server wraps it as `results` (ArrayWrapper), not the
  // singular `result` used for object responses — read `results` or rows never surface.
  const res = await httpService.Get<Response & { results?: MemberActionAuditView[] }>(
    `/club/${clubId}/member-action-audit`,
    { params: { capability: capabilityKey, userId: params.userId, status: params.status }, signal }
  )
  return res.results ?? []
}

/**
 * A club's member-action history (with status) across its ACTIVE member-acting capabilities —
 * generalized, not tied to any single capability. The caller passes the active capabilities (from
 * {@link useClubTools}); this fetches each capability's audit and merges them newest-first, tagging
 * every row with its capability so a future capability surfaces here automatically.
 */
export const useMemberActionAudit = (
  clubId: number | string,
  capabilities: { key: string; label: string }[],
  params: MemberActionAuditParams,
  enabled: boolean
) =>
  useQuery(
    [
      CapabilityQueryKeys.CLUB_MEMBER_ACTION_AUDIT,
      clubId,
      capabilities
        .map((c) => c.key)
        .sort()
        .join(','),
      params.userId ?? '',
      params.status ?? '',
    ],
    async ({ signal }): Promise<MemberActionAuditEntry[]> => {
      const perCapability = await Promise.all(
        capabilities.map(async (cap) => {
          const rows = await fetchMemberActionAudit(clubId, cap.key, params, signal)
          return rows.map((row) => ({ ...row, capabilityKey: cap.key, capabilityLabel: cap.label }))
        })
      )
      return perCapability.flat().sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? ''))
    },
    {
      enabled: enabled && !!clubId && capabilities.length > 0,
      retry: false,
      refetchOnWindowFocus: false,
    }
  )
